import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const MODES = [
  { title: "WEEKLY PAYOUTS", note: "Recurring weekly distribution on selected products" },
  { title: "MONTHLY PAYOUTS", note: "Structured monthly payout options where applicable" },
  { title: "AI-MANAGED PRODUCTS", note: "Strategies managed by intelligent systems" },
  { title: "NO MANUAL TRADING REQUIRED", note: "Participate without actively trading markets" },
];

const RATES = {
  weekly: { label: "Weekly", low: 0.3, high: 0.7 },
  monthly: { label: "Monthly", low: 1.4, high: 3.0 },
};

export default function RegularIncome() {
  const [amount, setAmount] = useState(1000);
  const [mode, setMode] = useState("weekly");
  const rate = RATES[mode];

  return (
    <section className="relative py-24 sm:py-32 border-t border-white/[0.06]" data-testid="regular-income-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="Chapter 03"
          kicker="Income Architecture"
          title="Built for Regular Earning Opportunities."
          subtitle="Selected Winter Protocol products provide structured earning and payout opportunities, designed around recurring schedules."
        />

        <div className="mt-14">
          {MODES.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.06}>
              <div
                className="group flex items-center justify-between gap-6 border-b border-white/[0.08] py-7 sm:py-9 transition-colors duration-500 hover:border-[#D4AF37]/30"
                data-testid={`income-mode-${i + 1}`}
              >
                <div className="flex items-baseline gap-5 sm:gap-8">
                  <span className="font-mono text-xs text-[#3A3F4A]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white transition-colors duration-500 group-hover:text-[#F3E5AB]">
                      {m.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#636B78]">{m.note}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-6 w-6 shrink-0 text-[#3A3F4A] transition-all duration-500 group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 grid gap-8 glass-card p-7 sm:p-10 md:grid-cols-2" data-testid="income-calculator">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">
                Payout Rhythm Explorer
              </p>
              <div className="mt-5 flex gap-2">
                {Object.keys(RATES).map((key) => (
                  <button
                    key={key}
                    onClick={() => setMode(key)}
                    data-testid={`income-mode-toggle-${key}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                      mode === key
                        ? "bg-[#D4AF37] text-[#08080A]"
                        : "border border-white/10 text-[#99A1AF] hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {RATES[key].label}
                  </button>
                ))}
              </div>
              <div className="mt-6">
                <label className="font-mono text-xs text-[#99A1AF]" htmlFor="income-amount">
                  Allocation amount
                </label>
                <input
                  id="income-amount"
                  type="range"
                  min="100"
                  max="10000"
                  step="100"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  data-testid="income-calculator-input"
                  className="mt-3 w-full accent-[#D4AF37]"
                />
                <p className="mt-2 font-mono text-sm text-white">${Number(amount).toLocaleString()}</p>
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-2xl border border-white/[0.08] bg-[#0B0B0F] p-6">
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">
                Illustrative {rate.label} Range
              </p>
              <p className="mt-3 font-display text-3xl sm:text-4xl font-bold text-gold-gradient" data-testid="income-calculator-result">
                ${((amount * rate.low) / 100).toFixed(2)} – ${((amount * rate.high) / 100).toFixed(2)}
              </p>
              <p className="mt-4 text-[11px] leading-relaxed text-[#636B78]">
                Illustrative figures only, shown to explain payout rhythm. Earnings are not guaranteed
                and depend on product terms and market conditions.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
